<template>
  <fieldset class="ship-to-fields">
    <legend>Indirizzo di consegna</legend>

    <div class="field field-street">
      <label>Via e numero civico<span>*</span></label>
      <input type="text" v-model="local.ship_to_street" required maxlength="254" />
    </div>

    <div class="field field-zip">
      <label>CAP<span>*</span></label>
      <input type="text" v-model="local.ship_to_zip_code" required pattern="\d{5}" maxlength="5" inputmode="numeric" />
    </div>

    <div class="field field-city">
      <label>Città<span>*</span></label>
      <input type="text" v-model="local.ship_to_city" required maxlength="64" />
    </div>

    <div class="field field-country">
      <label>Nazione<span>*</span></label>
      <select v-model="local.ship_to_country" required>
        <option value="IT">Italia</option>
        <option value="SM">San Marino</option>
      </select>
    </div>

    <div v-if="local.ship_to_country === 'IT'" class="field field-state">
      <label>Provincia<span>*</span></label>
      <input
        type="text"
        list="sap-italian-provinces"
        v-model="local.ship_to_state"
        @input="normalizeState"
        required
        maxlength="2"
        placeholder="es. BS"
        class="uppercase-input"
        :class="{ 'state-invalid': stateFeedback.invalid }"
      />
      <datalist id="sap-italian-provinces">
        <option v-for="p in provinceList" :key="p.code" :value="p.code">{{ p.name }}</option>
      </datalist>
      <small v-if="stateFeedback.valid" class="state-hint-ok">
        ✓ {{ stateFeedback.name }}
      </small>
      <small v-else-if="stateFeedback.invalid" class="state-hint-ko">
        Codice provincia non riconosciuto — scegline uno dall'elenco
      </small>
      <small v-else>Digita la sigla (es. BS) o scegli dall'elenco.</small>
    </div>

    <div class="field field-name">
      <label>Nome destinatario<span>*</span></label>
      <input type="text" v-model="local.ship_to_address_name" required maxlength="254" />
    </div>

    <div class="field field-floor">
      <label>Consegna al piano o ritiro usato?</label>
      <select v-model="local.floor_delivery_option">
        <option value="N">No</option>
        <option value="S">Consegna al piano</option>
        <option value="R">Consegna al piano e ritiro usato</option>
      </select>
    </div>

    <div class="field field-notes">
      <label>Note per il corriere</label>
      <textarea v-model="local.ship_to_notes" maxlength="72" rows="2"></textarea>
    </div>
  </fieldset>
</template>

<script>
import { SAP_ITALIAN_PROVINCES } from '@/utils/province-sap.js'

export default {
  name: 'ShipToFields',
  props: {
    modelValue: {
      type: Object,
      required: true
    }
  },
  emits: ['update:modelValue'],
  data () {
    return {
      provinceList: SAP_ITALIAN_PROVINCES,
      local: {
        ship_to_street: this.modelValue.ship_to_street || '',
        ship_to_zip_code: this.modelValue.ship_to_zip_code || '',
        ship_to_city: this.modelValue.ship_to_city || '',
        ship_to_country: this.modelValue.ship_to_country || 'IT',
        ship_to_state: this.modelValue.ship_to_state || '',
        ship_to_address_name: this.modelValue.ship_to_address_name || '',
        ship_to_notes: this.modelValue.ship_to_notes || '',
        floor_delivery_option: this.modelValue.floor_delivery_option || 'N'
      }
    }
  },
  computed: {
    stateFeedback () {
      const code = (this.local.ship_to_state || '').trim().toUpperCase()
      if (!code) return { valid: false, invalid: false, name: null }
      const match = this.provinceList.find((p) => p.code === code)
      if (match) return { valid: true, invalid: false, name: match.name }
      if (code.length === 2) return { valid: false, invalid: true, name: null }
      return { valid: false, invalid: false, name: null }
    }
  },
  methods: {
    normalizeState () {
      // Normalizza in tempo reale: uppercase + trim. Evita che l'utente debba
      // scrivere esattamente "BS" maiuscolo o selezionare dal datalist per far
      // passare la validazione (altrimenti "bs" o "Bs" fallirebbero).
      const cleaned = (this.local.ship_to_state || '').trim().toUpperCase()
      if (cleaned !== this.local.ship_to_state) {
        this.local.ship_to_state = cleaned
      }
    }
  },
  watch: {
    local: {
      deep: true,
      handler (v) {
        this.$emit('update:modelValue', { ...v })
      }
    },
    'local.ship_to_country' (v) {
      // Se l'utente passa a SM, azzera la provincia (non applicabile a San Marino).
      if (v === 'SM') {
        this.local.ship_to_state = ''
      }
    }
  }
}
</script>

<style scoped>
.ship-to-fields {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 1rem 1.25rem 1.25rem;
  margin: 1rem 0;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  column-gap: 1rem;
  row-gap: 0.85rem;
  background: #fafafa;
}
.ship-to-fields legend {
  grid-column: 1 / -1;
  font-weight: 600;
  font-size: 1.05em;
  padding: 0 0.25rem;
  margin-bottom: 0.25rem;
}
.ship-to-fields .field {
  display: flex;
  flex-direction: column;
  grid-column: span 12;
  min-width: 0;
}
.ship-to-fields .field-street     { grid-column: span 12; }
.ship-to-fields .field-zip        { grid-column: span 3; }
.ship-to-fields .field-city       { grid-column: span 9; }
.ship-to-fields .field-country    { grid-column: span 6; }
.ship-to-fields .field-state      { grid-column: span 6; }
.ship-to-fields .field-name       { grid-column: span 12; }
.ship-to-fields .field-floor      { grid-column: span 12; }
.ship-to-fields .field-notes      { grid-column: span 12; }

.ship-to-fields label {
  display: block;
  font-size: 0.88em;
  margin-bottom: 0.25rem;
  line-height: 1.2;
}
.ship-to-fields label span {
  color: #c00;
  margin-left: 2px;
}
.ship-to-fields label .hint {
  font-weight: 400;
  color: #888;
}
.ship-to-fields input,
.ship-to-fields select,
.ship-to-fields textarea {
  width: 100%;
  padding: 0.45rem 0.55rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95em;
  background: #fff;
  box-sizing: border-box;
}
.ship-to-fields textarea {
  resize: vertical;
}
.ship-to-fields small {
  display: block;
  font-size: 0.78em;
  color: #666;
  margin-top: 0.2rem;
}
.ship-to-fields .uppercase-input {
  text-transform: uppercase;
}
.ship-to-fields .state-invalid {
  border-color: #c00;
  background: #fff5f5;
}
.ship-to-fields .state-hint-ok {
  color: #2a7a2a;
  font-weight: 500;
}
.ship-to-fields .state-hint-ko {
  color: #c00;
  font-weight: 500;
}

/* Mobile: tutto a piena riga */
@media (max-width: 640px) {
  .ship-to-fields .field-zip,
  .ship-to-fields .field-city,
  .ship-to-fields .field-country,
  .ship-to-fields .field-state {
    grid-column: span 12;
  }
}
</style>
